import React, { ComponentProps, FC, memo } from 'react'

import { Carousel } from '@factorialco/factorial-one-react/dist/experimental'
import { Celebration as BaseCelebration } from '@factorialco/factorial-one-react/dist/experimental'
import { WeeklyCelebrationsDocument } from 'generated/resources/graphql'
import moment from 'moment'
import { useDebounceValue } from 'usehooks-ts'

import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { Widget, useWidgetsContext } from 'modules/core/lib/widgets'

import { buildHolidaysWidget } from 'modules/dashboard/components/Widgets/Holidays'

import Celebration from './Celebration'

const DEBOUNCE_LOADING_TIME = 500

const COLUMNS = {
  default: 1,
  lg: 4,
  md: 4,
  sm: 3,
  xs: 2,
} as const

const DOUBLE_COLUMNS: ComponentProps<typeof Carousel>['doubleColumns'] = [
  { index: 0, sizes: ['default', 'md', 'lg'] },
]

const WeeklyCelebrationsCarousel: FC = () => {
  const { access } = useCurrentSession()
  const startsOn = moment().utc(false).startOf('day')
  const endsOn = startsOn.clone().add(7, 'days')

  const { widgetStates } = useWidgetsContext()

  const isHolidaysWidgetEmpty = widgetStates?.['holidays'] === 'empty_and_not_shown'

  const { data, loading } = useQuery(WeeklyCelebrationsDocument, {
    variables: {
      accessId: access.id,
      startsOn: startsOn.toISOString(),
      endsOn: endsOn.toISOString(),
    },
  })

  const celebrationIds =
    data?.calendar.calendarEventsConnection.nodes.map((celebration) => celebration.id) ?? []

  const HolidaysWidget = isHolidaysWidgetEmpty ? null : (
    <ErrorBoundary customCrashComponent={<></>}>
      <Widget widget={buildHolidaysWidget()} fullHeight />
    </ErrorBoundary>
  )

  const [showLoader] = useDebounceValue(!celebrationIds.length && loading, DEBOUNCE_LOADING_TIME)

  if (showLoader)
    return (
      <Carousel columns={COLUMNS}>
        {new Array(8).fill(null).map((_, i) => (
          <BaseCelebration.Skeleton key={i} />
        ))}
      </Carousel>
    )

  return (
    <Carousel columns={COLUMNS} doubleColumns={isHolidaysWidgetEmpty ? undefined : DOUBLE_COLUMNS}>
      {HolidaysWidget}
      {celebrationIds.map((celebrationId, index) => (
        <Celebration key={celebrationId} id={celebrationId} index={index} />
      ))}
    </Carousel>
  )
}

export default memo(WeeklyCelebrationsCarousel)
